import type { AssignableRole } from "@unbox/api";
import { HelpCircle, Mail01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useListData } from "react-stately";
import { useSendInvite } from "@/api/admin/send-invite";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { AlertFloating } from "@/components/application/alerts/alerts";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { MultiSelect } from "@/components/base/select/multi-select";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { FormField, FormItem } from "@/components/ui/form";
import { usePermissions } from "@/hooks/use-permissions";
import { WorkspaceRoleTree } from "@/pages/admin/components/workspace-role-tree";
import { useWorkspaceRoleTree } from "@/pages/admin/hooks/use-workspace-role-tree";
import { error, success } from "@/providers/ToastProvider";

// Roles are chosen per workspace / per merchant (UNB-2001).
type FormValues = {
  role: AssignableRole;
  email: string;
};

export const InviteUserModal = () => {
  const [open, setOpen] = useState(false);
  const { invitableRoles } = usePermissions();
  const form = useForm<FormValues>({
    defaultValues: { role: "user", email: "" },
  });
  const { mutateAsync } = useSendInvite();
  const { data: workspacesData } = useAdminWorkspaces(
    { offset: 0, limit: 100 },
    { column: "name", direction: "asc" },
    {},
  );

  const roleTree = useWorkspaceRoleTree();
  const addWorkspaceItems = useListData<{ id: string; label: string }>({ initialItems: [] });

  useEffect(() => {
    form.reset();
    roleTree.setGrants({});
    for (const item of addWorkspaceItems.items) addWorkspaceItems.remove(item.id);
  }, [open]);

  const workspacesById = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        name: string;
        logoUrl?: string | null;
        merchants: { id: string; name: string; logoUrl?: string | null }[];
      }
    >();
    for (const ws of workspacesData?.workspaces ?? []) {
      map.set(ws.id, { id: ws.id, name: ws.name, logoUrl: ws.logoUrl, merchants: ws.merchants });
    }
    return map;
  }, [workspacesData]);

  const availableWorkspacesToAdd = useMemo(
    () => (workspacesData?.workspaces ?? []).filter((ws) => !(ws.id in roleTree.grants)),
    [workspacesData, roleTree.grants],
  );

  const onSubmit: SubmitHandler<FormValues> = async (payload) => {
    const grants = roleTree.grants;
    const workspaceIds = Object.keys(grants);
    if (workspaceIds.length === 0) {
      error("Select at least one workspace and at least one merchant");
      return;
    }
    for (const wsId of workspaceIds) {
      // Workspace admins need no brands (UNB-2001); everyone else needs ≥1.
      if (!grants[wsId].workspaceAdmin && Object.keys(grants[wsId].merchants).length === 0) {
        error(`Select at least one merchant in "${workspacesById.get(wsId)?.name ?? wsId}"`);
        return;
      }
    }

    // Top-level assignableRole is required by the invite route and used as the
    // fallback when there are no roleGrants; roleGrants is the source of truth on
    // accept. Send the highest role granted so both agree.
    const topRole: AssignableRole = workspaceIds.some((w) => grants[w].workspaceAdmin)
      ? "workspace_admin"
      : workspaceIds.some((w) => Object.values(grants[w].merchants).some((r) => r === "merchant_admin"))
        ? "merchant_admin"
        : "user";

    // Workspace-admin grants store no brands — send an empty merchants map so the
    // invite doesn't materialise per-brand rows on accept (UNB-2001).
    const roleGrants = Object.fromEntries(
      Object.entries(grants).map(([wsId, g]) => [wsId, g.workspaceAdmin ? { workspaceAdmin: true, merchants: {} } : g]),
    );

    await mutateAsync({ email: payload.email.trim(), role: topRole, roleGrants });
    success("User invited");
    setOpen(false);
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button>Add new User</Button>
      {/* Not dismissable: an outside click would discard the whole form (UNB-2129). */}
      <ModalOverlay isDismissable={false}>
        <Modal className="dark-mode">
          <Dialog>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="min-w-[640px] relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
                <CloseButton
                  onClick={() => setOpen(false)}
                  theme="light"
                  size="lg"
                  className="absolute top-3 right-3"
                />
                <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                  <div className="z-10 flex flex-col gap-0.5">
                    <Heading slot="title" className="text-md font-semibold text-primary">
                      Add new User
                    </Heading>
                    <p className="text-sm text-tertiary">Invite someone and configure what they can access.</p>
                  </div>
                </div>
                <div className="h-5 w-full" />
                <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Input isRequired label="Email" placeholder="Email" {...field} />
                      </FormItem>
                    )}
                  />

                  <FormItem className="flex flex-col gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-primary">Workspaces</span>
                      <Tooltip title="Assign the user to one or more workspaces, then choose their role at the workspace level or per merchant inside it.">
                        <TooltipTrigger>
                          <HelpCircle className="size-3.5 text-tertiary" />
                        </TooltipTrigger>
                      </Tooltip>
                    </div>

                    <WorkspaceRoleTree
                      grants={roleTree.grants}
                      workspacesById={workspacesById}
                      invitableRoles={invitableRoles}
                      onRemoveWorkspace={roleTree.removeWorkspace}
                      onToggleBrand={roleTree.toggleBrand}
                      onSetWorkspaceRole={roleTree.setWorkspaceRole}
                      onSetMerchantRole={roleTree.setMerchantRole}
                    />

                    {availableWorkspacesToAdd.length > 0 && (
                      <MultiSelect
                        aria-label="Add workspace"
                        placeholder="Search Workspaces"
                        size="md"
                        items={availableWorkspacesToAdd.map((w) => ({ id: w.id, label: w.name }))}
                        selectedItems={addWorkspaceItems}
                        onItemInserted={(key) => {
                          const ws = workspacesById.get(String(key));
                          roleTree.addWorkspace(
                            String(key),
                            (ws?.merchants ?? []).map((m) => m.id),
                          );
                          addWorkspaceItems.remove(String(key));
                        }}
                        popoverClassName="dark-mode"
                      >
                        {(item) => (
                          <MultiSelect.Item key={item.id} id={item.id} className="dark-mode">
                            {item.label}
                          </MultiSelect.Item>
                        )}
                      </MultiSelect>
                    )}
                  </FormItem>

                  <AlertFloating
                    title=""
                    color="gray"
                    description="This will auto send an email to the user asking them to set up their account."
                    icon={Mail01}
                  />
                </div>
                <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                  <Button color="secondary" size="lg" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button color="primary" size="lg" type="submit">
                    Add new User
                  </Button>
                </div>
              </div>
            </form>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
